import * as React from 'react';
import { useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Popover, Select } from 'antd';
import { FormInstance } from 'antd/lib/form';
import {
	ClockCircleOutlined,
	DeleteOutlined,
	MinusCircleOutlined,
	PlusOutlined,
	UserOutlined,
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import OWButton from '../ow_button/OWButton';
import InlineEdit from '../ow_inline_todo_list/InlineEdit';
import DeliverableAssigneeSelect from '../deliverable_assignee_select/DeliverableAssigneeSelect';
import { AssignedToDisplay } from '../deliverable_list_item/DeliverableListItem';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import { formatCurrency } from '../../utils/DeliverableUtils';
import { cn } from '../../utils/ClassNamesUtils';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

// Field blocks for a project template's deliverables and tasks. Presentational: they
// read and write the enclosing antd Form at the `name` they are given, so the task list
// serves both 'taskTemplates' and a deliverable modal's own 'tasks'.
//
// Rows carry no required rule — a blank row left behind is dropped on save by the
// clean* helpers in ProjectTemplateForm rather than blocking it. The deliverable modal
// is the exception: it requires a name, since Add is a deliberate act.
//
// DeliverableTasks, the equivalent row on a real project, is not reusable here — it
// takes a projectId/deliverableId and writes each edit through
// projectTasksRestCrudThunks, where a template task has no server row behind it.

const { TextArea } = Input;

// A path into the enclosing form: 'taskTemplates', or ['tasks'] inside a Form.List.
type FormItemName = string | (string | number)[];

// value/onChange as antd's Form.Item injects them into a custom control.
interface FormControlProps<T> {
	value?: T;
	onChange?: (value: T) => void;
}

// A template task as the form holds it. No status or due DATE — a template has no
// calendar; daysForDue becomes a real date when a project is created from it.
interface TemplateTask {
	title?: string;
	assignTo?: string;
	daysForDue?: number;
}

interface TemplateDeliverable {
	id?: string;
	name?: string;
	description?: string;
	budget?: number;
	daysForDue?: number;
	// Plural, unlike a task's: a deliverable carries a list of emails.
	assignTo?: string[];
	dependsOnId?: string;
	tasks?: TemplateTask[];
}

interface DependencyOption {
	value: string;
	label: string;
}

// Shared by every row's assignee picker: a target collection name has to exist in the
// buyer_contacts slice's standardInitialStateGenerator list, so it cannot be per-row.
// Safe — OWAsyncSelect resolves a selected value's label from records, not from here.
const TEMPLATE_DELIVERABLE_ASSIGNEES_TC = 'PROJECT_TEMPLATE_CONTACTS_AUTOCOMPLETE';

const DURATION_TOOLTIP =
	'How many days this takes, counting the first day — 7 days on a project starting Oct 1 is due Oct 7. A deliverable that depends on another counts from that one’s due date instead: 14 days after a Oct 7 deliverable is due Oct 21. On a project with only an end date, counted backwards from it.';

// Width the deliverable and task lists line up to.
const ITEM_LIST_WIDTH_CLASS = 'max-w-[840px]';

const isBlank = (value) => value === undefined || value === null || value === '';

const describeDays = (days) => `${days} ${Number(days) === 1 ? 'day' : 'days'}`;

interface TemplateItemRowProps {
	index: number;
	onRemove: () => void;
	children: React.ReactNode;
}

export const TemplateItemRow: React.FC<TemplateItemRowProps> = ({ index, onRemove, children }) => (
	<div className="flex items-start gap-4">
		<div className="mt-1 w-4 text-character-secondary">{index + 1}</div>
		<div className="min-w-0 max-w-[640px] flex-1">{children}</div>
		<MinusCircleOutlined
			className="mt-2 cursor-pointer text-character-secondary"
			onClick={onRemove}
		/>
	</div>
);

interface TemplateItemAddButtonProps {
	label: string;
	onClick: () => void;
}

export const TemplateItemAddButton: React.FC<TemplateItemAddButtonProps> = ({ label, onClick }) => (
	<Form.Item className="mb-0">
		<Button type="dashed" onClick={onClick}>
			<PlusOutlined /> {label}
		</Button>
	</Form.Item>
);

const TemplateDurationField: React.FC<{ name: FormItemName }> = ({ name }) => (
	<Form.Item label="Takes (days)" name={name} tooltip={DURATION_TOOLTIP} className="mb-2 w-[160px]">
		<InputNumber min={0} precision={0} className="w-full" placeholder="e.g. 14" />
	</Form.Item>
);

interface TaskListFieldsProps {
	name: FormItemName;
	addLabel: string;
}

// A row's action triggers are real buttons so Enter/Space reach them and they can be
// tabbed to. Tailwind's preflight is off, so the UA button chrome has to be cleared by
// hand — without appearance-none/border-0/bg-transparent these render as grey boxes.
const TaskIconButton = React.forwardRef<HTMLButtonElement, any>(
	({ children, label, ...rest }, ref) => (
		<button
			{...rest}
			ref={ref}
			type="button"
			aria-label={label}
			title={label}
			className="flex cursor-pointer appearance-none items-center border-0 bg-transparent p-0 text-character-secondary"
		>
			{children}
		</button>
	)
);

// A row from "Add task" has no title, so it opens straight into edit. Safe because the
// form mounts only once its data has loaded (EditPage gates on `fetching`).
const TemplateTaskTitle: React.FC<FormControlProps<string>> = ({ value, onChange }) => {
	const [editing, setEditing] = useState(!value);

	const commit = (next) => {
		onChange((next || '').trim());
		setEditing(false);
	};

	const startEditing = () => setEditing(true);

	// role="button" + tabIndex make the display text reachable and activatable without a
	// mouse; it stays a div because it also has to wrap a long title.
	const handleKeyDown = (event) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			startEditing();
		}
	};

	return editing ? (
		<InlineEdit
			value={value || ''}
			placeholder="Task title"
			onEnter={() => {}}
			setValue={commit}
			className="bodyregular"
		/>
	) : (
		<div
			role="button"
			tabIndex={0}
			onClick={startEditing}
			onKeyDown={handleKeyDown}
			className={cn(
				'bodyregular pl-2 pt-1 hover:cursor-pointer',
				value ? 'text-character-primary' : 'text-character-disabled'
			)}
		>
			{value || 'Task title'}
		</div>
	);
};

// Days, not a date picker: a template has no calendar of its own.
const TemplateTaskDaysPopover: React.FC<FormControlProps<number>> = ({ value, onChange }) => {
	const [open, setOpen] = useState(false);

	const clearDays = () => {
		onChange(null);
		setOpen(false);
	};

	const content = (
		<div className="w-[220px]">
			<div className="footnoteregular mb-2 text-character-secondary">TAKES (DAYS)</div>
			<InputNumber
				min={0}
				precision={0}
				value={value}
				placeholder="e.g. 14"
				className="w-full"
				onChange={onChange}
			/>
			{isBlank(value) ? null : (
				<Button type="link" size="small" className="!mt-1 !px-0" onClick={clearDays}>
					Clear
				</Button>
			)}
		</div>
	);

	return (
		<Popover
			trigger="click"
			visible={open}
			onVisibleChange={setOpen}
			content={content}
			placement="bottomRight"
		>
			<TaskIconButton label="Time period">
				<ClockCircleOutlined />
			</TaskIconButton>
		</Popover>
	);
};

// getPopupContainer keeps the select's dropdown inside the popover — rendered at the
// body it counts as a click outside, and the popover closes before the pick registers.
const TemplateTaskAssigneePopover: React.FC<FormControlProps<string>> = ({ value, onChange }) => {
	const [open, setOpen] = useState(false);

	const handleAssigneeChange = (next) => {
		onChange(next);
		setOpen(false);
	};

	const content = (
		<div className="w-[288px]">
			<div className="footnoteregular mb-2 text-character-secondary">ASSIGN TO</div>
			<DeliverableAssigneeSelect
				multiple={false}
				targetCollectionName={TEMPLATE_DELIVERABLE_ASSIGNEES_TC}
				placeholder="Unassigned"
				value={value}
				onChange={handleAssigneeChange}
				selectProps={{ getPopupContainer: (trigger) => trigger.parentNode }}
			/>
		</div>
	);

	return (
		<Popover
			trigger="click"
			visible={open}
			onVisibleChange={setOpen}
			content={content}
			placement="bottomRight"
		>
			<TaskIconButton label="Assignee">
				<UserOutlined />
			</TaskIconButton>
		</Popover>
	);
};

const TemplateTaskDetailLine: React.FC<{ task: TemplateTask }> = ({ task }) => {
	const assignTo = nullSafeGet('assignTo', task);
	const daysForDue = nullSafeGet('daysForDue', task);
	const hasDays = !isBlank(daysForDue);
	if (!assignTo && !hasDays) {
		return null;
	}
	return (
		<div className="footnoteregular flex items-center gap-2 pl-2 text-character-secondary">
			{assignTo ? <UserNameWithEmail email={assignTo} mode="inline" /> : null}
			{assignTo && hasDays ? <span>·</span> : null}
			{hasDays ? <span>{describeDays(daysForDue)}</span> : null}
		</div>
	);
};

export const TemplateTaskListFields: React.FC<TaskListFieldsProps> = ({ name, addLabel }) => {
	// Watched only for the detail line, which needs both popover values at once.
	const form = Form.useFormInstance();
	const rows: TemplateTask[] = Form.useWatch(name, form) || [];

	return (
		<Form.List name={name}>
			{(fields, { add, remove }) => {
				const handleAddTask = () => add();
				return (
					<div className={ITEM_LIST_WIDTH_CLASS}>
						{fields.map(({ key, name: taskName, ...restField }, index) => {
							const handleRemoveTask = () => remove(taskName);
							return (
								<div key={key} className="group mb-2 flex flex-row items-start">
									<div className="footnoteregular w-4 pt-1 text-character-secondary">
										{index + 1}
									</div>
									<div className="min-w-0 flex-1">
										<div className="flex flex-row items-start">
											{/* antd loads after Tailwind, so its form-item margin and control
											    min-height have to be undone for the row to read as text. Width is
											    left to the title so the action cluster trails it. */}
											<Form.Item
												{...restField}
												name={[taskName, 'title']}
												className="!mb-0 min-w-0 [&_.ant-form-item-control-input]:min-h-0"
											>
												<TemplateTaskTitle />
											</Form.Item>
											{/* Faded rather than `hidden`, matching AddLabelChip: a display:none
											    element cannot take focus, so the buttons inside would drop out of
											    the tab order and group-focus-within could never fire.
											    pointer-events-none keeps the invisible icons unclickable. h-[22px]
											    + mt-1 matches the title's line box so they centre on the text. */}
											<div className="pointer-events-none ml-4 mt-1 flex h-[22px] flex-row items-center gap-4 text-character-secondary opacity-0 transition-opacity duration-150 hover:cursor-pointer group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100">
												<Form.Item {...restField} name={[taskName, 'daysForDue']} noStyle>
													<TemplateTaskDaysPopover />
												</Form.Item>
												<Form.Item {...restField} name={[taskName, 'assignTo']} noStyle>
													<TemplateTaskAssigneePopover />
												</Form.Item>
												<Popconfirm
													placement="topRight"
													title="Remove this task?"
													okText="Remove"
													okButtonProps={{ danger: true }}
													cancelText="Cancel"
													onConfirm={handleRemoveTask}
												>
													<TaskIconButton label="Remove task">
														<DeleteOutlined />
													</TaskIconButton>
												</Popconfirm>
											</div>
										</div>
										<TemplateTaskDetailLine task={rows[taskName] || {}} />
									</div>
								</div>
							);
						})}
						<TemplateItemAddButton label={addLabel} onClick={handleAddTask} />
					</div>
				);
			}}
		</Form.List>
	);
};

interface TemplateDeliverableModalProps {
	initialValues: TemplateDeliverable;
	currencyId: string;
	dependencyOptions: DependencyOption[];
	isNew: boolean;
	onCancel: () => void;
	onSave: (deliverable: TemplateDeliverable) => void;
}

// antd Modals portal out, so this Form is not nested in the template's <form>. Its
// values are handed back on save, which is what makes Cancel a no-op.
const TemplateDeliverableModal: React.FC<TemplateDeliverableModalProps> = ({
	initialValues,
	currencyId,
	dependencyOptions,
	isNew,
	onCancel,
	onSave,
}) => {
	const [form] = Form.useForm();

	const handleSave = () => {
		// The id and anything the modal does not edit ride along from the row as it was.
		form.validateFields().then((values) => onSave({ ...initialValues, ...values }));
	};

	return (
		<Modal
			visible={true}
			title={isNew ? 'Add deliverable' : 'Edit deliverable'}
			onCancel={onCancel}
			width={720}
			destroyOnClose={true}
			footer={[
				<Button key="cancel" onClick={onCancel}>
					Cancel
				</Button>,
				<OWButton key="save" type="primary" onClick={handleSave}>
					{isNew ? 'Add deliverable' : 'Save deliverable'}
				</OWButton>,
			]}
		>
			<Form form={form} layout="vertical" initialValues={initialValues} preserve={false}>
				<Form.Item
					label="Name"
					name="name"
					rules={[{ required: true, message: 'Give this deliverable a name' }]}
				>
					<Input />
				</Form.Item>
				<Form.Item label="Description" name="description">
					<TextArea autoSize={{ minRows: 2 }} />
				</Form.Item>
				<div className="flex items-start gap-4">
					<Form.Item label="Budget" name="budget" className="mb-2 min-w-0 flex-1">
						<InputNumber min={0} className="flex w-full" addonAfter={currencyId} />
					</Form.Item>
					<TemplateDurationField name="daysForDue" />
				</div>
				<Form.Item
					label="Default assignees"
					name="assignTo"
					tooltip="Everyone here is assigned to this deliverable when a project is created from the template. Assignees can still be changed on the project."
				>
					<DeliverableAssigneeSelect
						targetCollectionName={TEMPLATE_DELIVERABLE_ASSIGNEES_TC}
						placeholder="Nobody by default"
					/>
				</Form.Item>
				<Form.Item
					label="Depends on"
					name="dependsOnId"
					tooltip="The deliverable that has to finish first. This one's days then count from that deliverable's due date rather than from the project start, and the dependency carries onto the project so a later date change cascades."
				>
					<Select
						allowClear
						showSearch
						optionFilterProp="label"
						placeholder="No dependency"
						options={dependencyOptions}
					/>
				</Form.Item>
				<div className="bodymedium mb-2">Tasks</div>
				<TemplateTaskListFields name="tasks" addLabel="Add task" />
			</Form>
		</Modal>
	);
};

interface DeliverableSummaryRowProps {
	index: number;
	deliverable: TemplateDeliverable;
	currency: any;
	dependsOnName: string | null;
	onEdit: () => void;
	onRemove: () => void;
}

const DeliverableSummaryRow: React.FC<DeliverableSummaryRowProps> = ({
	index,
	deliverable,
	currency,
	dependsOnName,
	onEdit,
	onRemove,
}) => {
	const tasks = nullSafeGetOrElse('tasks', deliverable, []);
	const budget = nullSafeGet('budget', deliverable);
	const daysForDue = nullSafeGet('daysForDue', deliverable);
	const assignees = nullSafeGetOrElse('assignTo', deliverable, []);
	const description = nullSafeGet('description', deliverable);
	const formatBudget = (amount) =>
		currency && currency.symbolFormat ? currency.symbolFormat(amount, 0) : formatCurrency(amount);

	const meta = [
		isBlank(budget) ? null : formatBudget(budget),
		isBlank(daysForDue) ? null : describeDays(daysForDue),
		dependsOnName ? `After ${dependsOnName}` : null,
		tasks.length > 0 ? `${tasks.length} ${tasks.length === 1 ? 'task' : 'tasks'}` : null,
	].filter((part) => !!part);

	return (
		<div
			className={cn('flex items-start gap-4 px-4 py-3', {
				'border-0 border-t border-solid border-owgray': index > 0,
			})}
		>
			<div className="footnoteregular mt-1 w-4 text-character-secondary">{index + 1}</div>
			<div className="min-w-0 flex-1">
				<div className="bodymedium truncate text-character-primary">
					{nullSafeGetOrElse('name', deliverable, 'Untitled deliverable')}
				</div>
				{description ? (
					<div className="footnoteregular truncate text-character-secondary">{description}</div>
				) : null}
				{meta.length > 0 ? (
					<div className="footnoteregular text-character-secondary">{meta.join(' · ')}</div>
				) : null}
				{assignees.length > 0 ? (
					<div className="mt-1">
						<AssignedToDisplay emails={assignees} />
					</div>
				) : null}
			</div>
			{/* `!` modifiers: antd loads after Tailwind, and .ant-btn's padding and height
			    otherwise leave the link button's full box between Edit and remove. */}
			<div className="flex items-center gap-4">
				<Button type="link" className="!h-auto !p-0" onClick={onEdit}>
					Edit
				</Button>
				<Popconfirm
					placement="topRight"
					title="Remove this deliverable?"
					okText="Remove"
					okButtonProps={{ danger: true }}
					cancelText="Cancel"
					onConfirm={onRemove}
				>
					<TaskIconButton label="Remove deliverable">
						<MinusCircleOutlined />
					</TaskIconButton>
				</Popconfirm>
			</div>
		</div>
	);
};

interface DeliverableListFieldsProps {
	name: string;
	// Currency code shown on the budget input, e.g. 'USD'.
	currencyId: string;
	form: FormInstance;
}

// Which row the modal is editing: its position, or null for one that does not exist
// yet, alongside the values it opened with.
interface EditingDeliverable {
	index: number | null;
	values: TemplateDeliverable;
}

export const TemplateDeliverableListFields: React.FC<DeliverableListFieldsProps> = ({
	name,
	currencyId,
	form,
}) => {
	const rows: TemplateDeliverable[] = Form.useWatch(name, form) || [];
	const currency = getCurrency({ currencyId });
	const [editing, setEditing] = useState<EditingDeliverable>(null);

	// Siblings by stable id, minus itself — the one dependency the API is sure to reject.
	const dependencyOptions = (currentId): DependencyOption[] =>
		rows
			.map((row, index) => ({
				value: nullSafeGetOrElse('id', row, ''),
				label: `${index + 1}. ${nullSafeGetOrElse('name', row, 'Untitled deliverable')}`,
			}))
			.filter((option) => option.value && option.value !== currentId);

	// By id, not position, so it survives rows being added or removed.
	const dependsOnName = (deliverable) => {
		const dependsOnId = nullSafeGet('dependsOnId', deliverable);
		if (!dependsOnId) {
			return null;
		}
		const target = rows.find((row) => nullSafeGetOrElse('id', row, '') === dependsOnId);
		return target ? nullSafeGetOrElse('name', target, 'Untitled deliverable') : null;
	};

	// Form.List has add and remove but no update. Length is unchanged, so keys survive.
	const replaceRow = (index, values) => {
		const current = form.getFieldValue(name) || [];
		form.setFieldsValue({
			[name]: current.map((row, rowIndex) => (rowIndex === index ? values : row)),
		});
	};

	const closeModal = () => setEditing(null);

	return (
		<Form.List name={name}>
			{(fields, { add, remove }) => {
				// The id is minted once here and travels with the row for its lifetime.
				const handleAddDeliverable = () =>
					setEditing({ index: null, values: { id: uuidv4(), tasks: [], assignTo: [] } });

				const handleModalSave = (values) => {
					if (editing.index === null) {
						add(values);
					} else {
						replaceRow(editing.index, values);
					}
					closeModal();
				};

				return (
					<>
						{fields.length > 0 ? (
							<div className={cn('mb-2 border border-solid border-owgray', ITEM_LIST_WIDTH_CLASS)}>
								{fields.map(({ key, name: deliverableName }, index) => {
									// Indexed: a nullSafeGet path splits on '.', so a dynamic key mis-resolves.
									const deliverable = rows[deliverableName] || {};
									const handleEdit = () =>
										setEditing({ index: deliverableName, values: deliverable });
									const handleRemove = () => remove(deliverableName);
									return (
										<DeliverableSummaryRow
											key={key}
											index={index}
											deliverable={deliverable}
											currency={currency}
											dependsOnName={dependsOnName(deliverable)}
											onEdit={handleEdit}
											onRemove={handleRemove}
										/>
									);
								})}
							</div>
						) : null}
						<TemplateItemAddButton label="Add deliverable" onClick={handleAddDeliverable} />
						{editing ? (
							<TemplateDeliverableModal
								initialValues={editing.values}
								currencyId={currencyId}
								dependencyOptions={dependencyOptions(nullSafeGetOrElse('id', editing.values, ''))}
								isNew={editing.index === null}
								onCancel={closeModal}
								onSave={handleModalSave}
							/>
						) : null}
					</>
				);
			}}
		</Form.List>
	);
};
